"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HIERARCHY_ROW_ID_THRESHOLD = exports.HierarchyAdoptionValue = void 0;
exports.hierarchyReferenceCandidate = hierarchyReferenceCandidate;
exports.hierarchyPlacementKey = hierarchyPlacementKey;
exports.hierarchyAdoptionVeto = hierarchyAdoptionVeto;
exports.readHierarchyOccurrenceTrust = readHierarchyOccurrenceTrust;
const OrcadDsnCfbTypes_1 = require("./OrcadDsnCfbTypes");
var HierarchyAdoptionValue;
(function (HierarchyAdoptionValue) {
    HierarchyAdoptionValue["Reference"] = "reference";
    HierarchyAdoptionValue["Section"] = "section";
})(HierarchyAdoptionValue || (exports.HierarchyAdoptionValue = HierarchyAdoptionValue = {}));
exports.HIERARCHY_ROW_ID_THRESHOLD = 100_000;
function hierarchyReferenceCandidate(entry) {
    const viewName = entry.owningViewName ?? entry.parentViewName;
    const refInstId = entry.refInstId ?? entry.dbId;
    const reference = entry.name;
    const occurrenceId = entry.occurrenceId ?? (entry.parentViewName === undefined ? undefined : entry.recordId);
    if (entry.parentBranchOffset !== undefined ||
        viewName === undefined ||
        refInstId === undefined ||
        refInstId <= 0 ||
        entry.referenceStringOffset === undefined ||
        reference === undefined ||
        reference.length > 64 ||
        !(/^[A-Za-z][A-Za-z0-9_.-]*\d[A-Za-z0-9_.-]*$/.test(reference) ||
            /^[A-Za-z]+\?$/.test(reference) ||
            /^\d+$/.test(reference))) {
        return undefined;
    }
    return {
        refInstId,
        reference,
        viewName,
        offset: entry.referenceStringOffset,
        occurrenceId,
        branchOffset: entry.ownershipBranchOffset ?? entry.parentBranchOffset,
    };
}
function hierarchyPlacementKey(view, id) {
    return JSON.stringify([view.toUpperCase(), id]);
}
function hierarchyAdoptionVeto(trust, stream, view, id, branchOffset, occurrenceId, value = HierarchyAdoptionValue.Reference) {
    if (trust.streamVetoes.has(stream)) {
        return 'stream scope is unknowable';
    }
    if (view !== undefined && trust.viewVetoes.has(view.toUpperCase())) {
        return 'view has an untrusted branch';
    }
    if (id !== undefined && trust.tombstones.get(stream)?.has(id)) {
        return 'identity was claimed by an untrusted branch';
    }
    if (branchOffset !== undefined &&
        id !== undefined &&
        (occurrenceId === undefined ?
            trust.branchReferenceConflicts.has(JSON.stringify([stream, branchOffset, id]))
            : trust.conflicts.get(stream)?.has(JSON.stringify([branchOffset, occurrenceId, id])))) {
        return 'hierarchy-reference-conflict within one occurrence path';
    }
    if (branchOffset === undefined &&
        view !== undefined &&
        id !== undefined &&
        value === HierarchyAdoptionValue.Section &&
        trust.sectionConflicts.has(hierarchyPlacementKey(view, id))) {
        return 'hierarchy-section-conflict for decoded view/identity';
    }
    return undefined;
}
function readHierarchyOccurrenceTrust(document) {
    const trustedEntries = new Set();
    const rejectedEntries = [];
    const tombstones = new Map();
    const viewVetoes = new Set();
    const streamVetoes = new Set();
    const conflicts = new Map();
    const branchReferenceConflicts = new Set();
    const sectionConflicts = new Set();
    const sectionValues = new Map();
    const branchReferenceValues = new Map();
    const viewsByStream = new Map();
    const library = document.streams.find((stream) => stream.parsed?.kind === 'library')?.parsed;
    const strings = library?.kind === 'library' ? library.stringCatalog : undefined;
    for (const stream of document.streams) {
        if (stream.parsed?.kind !== 'hierarchy') {
            continue;
        }
        const branches = new Map();
        const identities = new Set();
        const ambiguous = new Set();
        const associatedViews = new Set([stream.parsed.viewName.toUpperCase()]);
        const pathCounts = new Map();
        for (const entry of stream.parsed.entries) {
            const view = entry.owningViewName ?? entry.parentViewName;
            const id = entry.refInstId ?? entry.dbId;
            if (view === undefined || id === undefined || entry.referenceStringOffset === undefined) {
                continue;
            }
            const key = hierarchyPlacementKey(view, id);
            const index = entry.sectionSelectorStringIndex;
            if (index !== undefined) {
                const selector = strings?.[index] ?? index;
                const sections = sectionValues.get(key) ?? new Set();
                if (selector !== '') {
                    sections.add(selector);
                }
                sectionValues.set(key, sections);
                if (sections.size > 1) {
                    sectionConflicts.add(key);
                }
            }
            const branch = entry.ownershipBranchOffset ?? entry.parentBranchOffset;
            if (branch !== undefined) {
                const occurrenceId = entry.occurrenceId ?? entry.recordId;
                const path = JSON.stringify([branch, occurrenceId, id]);
                const pathCount = (pathCounts.get(path) ?? 0) + 1;
                pathCounts.set(path, pathCount);
                const branchKey = JSON.stringify([stream.path, branch, id]);
                const branchValues = branchReferenceValues.get(branchKey) ?? new Set();
                branchValues.add(entry.name);
                branchReferenceValues.set(branchKey, branchValues);
                if (branchValues.size > 1) {
                    branchReferenceConflicts.add(branchKey);
                }
                if (pathCount > 1) {
                    ambiguous.add(path);
                    branchReferenceConflicts.add(branchKey);
                    sectionConflicts.add(key);
                }
            }
        }
        tombstones.set(stream.path, identities);
        conflicts.set(stream.path, ambiguous);
        viewsByStream.set(stream.path, associatedViews);
        for (const branch of stream.parsed.ownershipBranches ?? []) {
            const existing = branches.get(branch.offset);
            if (existing !== undefined) {
                existing.trusted = false;
            }
            else {
                branches.set(branch.offset, {
                    trusted: branch.state === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyBranchDecodeState.Resolved && branch.viewName !== undefined,
                    viewName: branch.viewName,
                    claims: new Map(),
                });
            }
            if (branch.viewName !== undefined) {
                associatedViews.add(branch.viewName.toUpperCase());
            }
        }
        const rows = stream.parsed.entries.filter((entry) => entry.ownershipState !== undefined || entry.ownershipBranchOffset !== undefined);
        for (const entry of rows) {
            const branch = entry.ownershipBranchOffset === undefined ? undefined : branches.get(entry.ownershipBranchOffset);
            if (branch !== undefined &&
                (entry.ownershipState !== OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.HierarchyOwnershipState.Resolved ||
                    entry.owningViewName === undefined ||
                    entry.sectionSelectorStringIndex === undefined)) {
                branch.trusted = false;
            }
            if (entry.owningViewName !== undefined) {
                associatedViews.add(entry.owningViewName.toUpperCase());
            }
        }
        for (const branch of branches.values()) {
            if (!branch.trusted) {
                if (branch.viewName === undefined) {
                    streamVetoes.add(stream.path);
                }
                else {
                    viewVetoes.add(branch.viewName.toUpperCase());
                }
            }
        }
        for (const entry of rows) {
            const branch = entry.ownershipBranchOffset === undefined ? undefined : branches.get(entry.ownershipBranchOffset);
            const id = entry.refInstId ?? entry.dbId;
            if (branch?.trusted === true) {
                trustedEntries.add(entry);
                if (id !== undefined) {
                    const path = JSON.stringify([entry.occurrenceId, id]);
                    const values = branch.claims.get(path) ?? {
                        id,
                        occurrenceId: entry.occurrenceId,
                        count: 0,
                        references: new Set(),
                        selectors: new Set(),
                    };
                    values.count++;
                    if (entry.name !== undefined && entry.name !== '') {
                        values.references.add(entry.name);
                    }
                    const index = entry.sectionSelectorStringIndex;
                    if (index !== undefined) {
                        const selector = strings?.[index] ?? index;
                        if (selector !== '') {
                            values.selectors.add(selector);
                        }
                    }
                    branch.claims.set(path, values);
                }
            }
            else {
                rejectedEntries.push({ streamPath: stream.path, entry });
                if (id !== undefined) {
                    identities.add(id);
                }
                if (branch === undefined) {
                    streamVetoes.add(stream.path);
                }
            }
        }
        for (const [offset, branch] of branches) {
            if (branch.viewName === undefined) {
                continue;
            }
            for (const values of branch.claims.values()) {
                if (values.count > 1 || values.references.size > 1 || values.selectors.size > 1) {
                    ambiguous.add(JSON.stringify([offset, values.occurrenceId, values.id]));
                    branchReferenceConflicts.add(JSON.stringify([stream.path, offset, values.id]));
                    sectionConflicts.add(hierarchyPlacementKey(branch.viewName, values.id));
                }
            }
        }
    }
    return {
        trustedEntries,
        tombstones,
        viewVetoes,
        streamVetoes,
        conflicts,
        branchReferenceConflicts,
        sectionConflicts,
        viewsByStream,
        rejectedEntries,
    };
}
//# sourceMappingURL=OrcadDsnCfbOccurrenceOwnership.js.map